import { Element, useEditor, useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { rem } from "polished";
import { useSelector } from "react-redux";
import type { Recording } from "src/types";
import styled from "styled-components";

import BlockFloatingOptions from "@/content-hub/editor/floating-options/block-floating-options";
import { getAllRecordings } from "@/content-hub/store/selectors";
import RichText from "@/content-hub/theme/primitives/rich-text";
import { hasDisplayDescriptionContent } from "@/content-hub/theme/primitives/video/has-display-description-content";
import {
  effectiveRecordingDisplay,
  resolveVideoDescriptionHtml,
} from "@/content-hub/theme/primitives/video/recording-metadata-merge";
import {
  EmbedVideoCard,
  RecordingVideoCard,
} from "@/content-hub/theme/primitives/video-cards";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/content-hub/theme/shared/block-wrapper";
import { inferYoutubePosterFromEmbedUrl } from "@/content-hub/utils/resolve-video-block-poster-for-details";

import { VideoGridSettings } from "./settings";
import {
  BlockPadding,
  EmptyBlockMessage,
  StyledFlex,
  VideoGridContainer,
} from "./styles";

export type videoProps =
  | {
      kind: "recording";
      recordingId: string;
      recording?: Recording;
      displayTitle?: string;
      displayDescription?: string;
      displayThumbnail?: string;
    }
  | {
      kind: "embed";
      url: string;
      id: string;
      thumbnail?: string;
      title?: string;
      displayTitle?: string;
      displayDescription?: string;
      displayThumbnail?: string;
    };

export type VideoGridProps = {
  videoTitleEnabled?: boolean;
  paginationEnabled?: boolean;
  singleRowEnabled?: boolean;
  videoList?: (string | videoProps)[];
};

export const VIDEO_GRID_DISPLAY_NAME = "Video Grid";

export default function VideoGrid(props: VideoGridProps) {
  const recordings = useSelector(getAllRecordings);
  const videoList = props.videoList ?? [];

  const {
    id,
    connectors: { connect },
  } = useNode();

  const { isEnabled } = useEditor((editorQuery) => ({
    isEnabled: editorQuery.isEnabled(),
  }));

  const flags = useFlags();
  const directVideoSharingEnabled = Boolean(flags.contentHubDirectVideoSharing);

  if (!isEnabled && videoList.length === 0 && recordings.length === 0) {
    return null;
  }

  const getRecordingCard = (video: videoProps & { kind: "recording" }) => {
    const { recordingId, displayTitle, displayThumbnail } = video;
    const recording = recordings.find(
      (recording) => recording.id === recordingId
    );
    if (recording) {
      const metadata = {
        displayTitle,
        displayDescription: video.displayDescription,
        displayThumbnail,
      };
      const merged = effectiveRecordingDisplay(recording, metadata);
      const descriptionHtml = resolveVideoDescriptionHtml(recording, metadata);
      const overlayDescriptionHtml =
        directVideoSharingEnabled &&
        !isEnabled &&
        hasDisplayDescriptionContent(descriptionHtml)
          ? descriptionHtml
          : undefined;

      return (
        <Container key={recordingId} data-video-item>
          <RecordingVideoCard
            showCaption={props.videoTitleEnabled}
            recording={recording}
            videoId={directVideoSharingEnabled ? recording.id : undefined}
            aspectRatio="wide"
            displayTitle={merged.title || null}
            displayThumbnailUrl={merged.thumbnail || null}
            overlayDescriptionHtml={overlayDescriptionHtml}
          />
        </Container>
      );
    }
    return null;
  };

  const getEmbedCard = (video: videoProps & { kind: "embed" }) => {
    const {
      id,
      url,
      thumbnail,
      title,
      displayTitle,
      displayDescription,
      displayThumbnail,
    } = video;
    const metadata = {
      displayTitle,
      displayDescription,
      displayThumbnail,
    };
    const embedDisplayTitle = (displayTitle ?? title) || "";
    const customEmbedThumb = displayThumbnail?.trim();
    const displayEmbedThumbnail =
      customEmbedThumb ||
      thumbnail ||
      inferYoutubePosterFromEmbedUrl(url ?? "") ||
      undefined;
    const descriptionHtml = resolveVideoDescriptionHtml(undefined, metadata);
    const overlayDescriptionHtml =
      directVideoSharingEnabled &&
      !isEnabled &&
      hasDisplayDescriptionContent(descriptionHtml)
        ? descriptionHtml
        : undefined;

    return (
      <Container key={id} data-video-item>
        <EmbedVideoCard
          url={url}
          thumbnail={displayEmbedThumbnail}
          title={embedDisplayTitle}
          aspectRatio="wide"
          showCaption={props.videoTitleEnabled}
          videoId={directVideoSharingEnabled ? id : undefined}
          overlayDescriptionHtml={overlayDescriptionHtml}
        />
      </Container>
    );
  };

  const videoCards = videoList.reduce((_videoCards, currentVideo) => {
    if (typeof currentVideo === "string") return _videoCards;

    const videoCard =
      currentVideo.kind === "recording"
        ? getRecordingCard(currentVideo)
        : getEmbedCard(currentVideo);

    return videoCard ? [..._videoCards, videoCard] : _videoCards;
  }, []);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={id} data-section-id={id}>
      <BlockMaxWidthWrapper>
        <BlockPadding>
          <StyledFlex>
            <Element
              id="video-grid-title"
              is={RichText}
              text={`<h3 style="text-align:center;">RingCentral Events Group Demo</h3><p style="text-align:center;">Most events and webinars are forgettable—generic slides, awkward transitions, and the same predictable format. But your events should stand out. Join an action packed session with Jordan Arnold, Head of Sales and Partnerships at RingCentral, where he’ll break down how to up-level your events. </p>`}
            />
          </StyledFlex>

          {isEnabled && videoCards.length === 0 ? (
            <EmptyBlockMessage>
              No videos selected. Please choose videos from the block&apos;s
              settings
            </EmptyBlockMessage>
          ) : (
            <VideoGridContainer>{videoCards}</VideoGridContainer>
          )}
        </BlockPadding>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

VideoGrid.craft = {
  props: {
    videoList: [],
    videoTitleEnabled: true,
  },
  custom: {
    displayName: "Video Grid",
    settingsTitle: "selection-menu.selection.blocks.video-grid",
  },
  related: {
    settings: VideoGridSettings,
    leftOutlineOptions: BlockFloatingOptions,
  },
  rules: {
    canMoveIn: () => false,
  },
};

const Container = styled.div`
  overflow: hidden;
  position: relative;
  max-width: ${rem(282)};
  max-height: ${rem(282)};
`;
